import React from 'react';
import {
    Text,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { useNavigate } from 'react-router-dom';
import useStore from '../../store';
import { UserMedia } from '../../types';
import { LazyImage } from './LazyImage';

const useStyles = makeStyles({
    mediaEntry: {
        position: 'relative',
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        width: 'fit-content',
        touchAction: 'manipulation',
        ':active': {
            transform: 'scale(0.98)',
        },
        '@media (min-width: 768px)': {
            ':hover': {
                transform: 'scale(1.05)',
            },
            ':active': {
                transform: 'scale(1.05)',
            },
        },
    },
    coverImage: {
        width: '100px', // Smaller base size
        height: '150px',
        objectFit: 'cover',
        borderRadius: tokens.borderRadiusSmall,
        display: 'block',
        '@media (min-width: 460px)': {
            width: '120px',
            height: '180px',
        },
        '@media (min-width: 768px)': {
            width: '140px',
            height: '210px',
        },
        '@media (min-width: 1024px)': {
            width: '160px',
            height: '240px',
        },
    },
    imagePlaceholder: {
        width: '100px', // Match coverImage
        height: '150px',
        backgroundColor: tokens.colorNeutralBackground3,
        borderRadius: tokens.borderRadiusSmall,
        display: 'block',
        '@media (min-width: 460px)': {
            width: '120px',
            height: '180px',
        },
        '@media (min-width: 768px)': {
            width: '140px',
            height: '210px',
        },
        '@media (min-width: 1024px)': {
            width: '160px',
            height: '240px',
        },
    },
    imageOverlay: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8) 30%, rgba(0, 0, 0, 0.95))',
        padding: tokens.spacingVerticalXXS, // Minimum padding
        borderBottomLeftRadius: tokens.borderRadiusSmall,
        borderBottomRightRadius: tokens.borderRadiusSmall,
        boxSizing: 'border-box',
        '@media (min-width: 460px)': {
            padding: tokens.spacingVerticalXS,
        },
        '@media (min-width: 768px)': {
            padding: tokens.spacingVerticalS,
        },
    },
    overlayContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS, // Reduced gap
    },
    overlayTitle: {
        color: 'white',
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
        lineHeight: tokens.lineHeightBase200,
        display: '-webkit-box',
        WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
        ':hover': {
            WebkitLineClamp: 'none',
        },
        '@media (min-width: 460px)': {
            fontSize: tokens.fontSizeBase200,
            lineHeight: tokens.lineHeightBase300,
        },
        '@media (min-width: 768px)': {
            fontSize: tokens.fontSizeBase300
        },
    },
    overlayStats: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)',
        width: '100%',
    },
    statusText: {
        fontSize: tokens.fontSizeBase100,
        opacity: 0.9,
        textTransform: 'capitalize',
    },
    scoreText: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
    },
});

interface MediaEntryProps {
    entry: UserMedia;
}

export const MediaEntry: React.FC<MediaEntryProps> = ({ entry }) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const setSelectedMediaId = useStore(state => state.setSelectedMediaId);

    const handleClick = () => {

        setSelectedMediaId(entry.id);
        navigate(`/media/${entry.id}`);
    };

    return (
        <div className={styles.mediaEntry} onClick={handleClick}>
            <LazyImage
                src={entry.coverImage.large}
                alt={entry.title.english || entry.title.romaji || 'Anime cover'}
                className={styles.coverImage}
                placeholderClassName={styles.imagePlaceholder}
            />
            <div className={styles.imageOverlay}>
                <div className={styles.overlayContent}>
                    <Text className={styles.overlayTitle}>
                        {entry.title.english || entry.title.romaji}
                    </Text>
                    <div className={styles.overlayStats}>
                        {entry.userStatus && (
                            <Text className={styles.statusText}>
                                {entry.userStatus.toLowerCase()}
                            </Text>
                        )}
                        {entry.userScore !== null && entry.userScore !== undefined && (
                            <Text className={styles.scoreText}>
                                ★{entry.userScore}
                            </Text>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};